// Port of TrailMap/CompositeMapProvider.swift (TrailBivvyKit): the app's map provider. It picks swisstopo, a regional
// official map or the fallback (Swift: Apple Maps; web: OpenStreetMap) for the area being shown, and assembles offline
// tile layers across every provider a route crosses.
//
// Selection (`providerFor(bounds)` / `providerAt(coordinate)`) uses the centre of the requested bounds:
// 1. swisstopo, then the `primary` regional providers in order, whose outline contains the centre;
// 2. the same providers when the centre is within `nearCoverageMarginMeters` of their outline;
// 3. `overview` regional providers (the Europe-wide TopPlusOpen fallback), exact then with the margin;
// 4. the fallback.
//
// Offline: every layer carries its provider's coverage and the planner skips tiles outside it, so
// `offlineTileLayersForRoute(level, route)` can return the union of all providers the route corridor touches.
//
// Web additions:
// - `mapStyle` adds the fallback's credit when a style draws the fallback base map (MapKit drew its own legal label).
// - `mosaic(preferences)`: every provider's layers at once, each limited to its coverage and ordered by the selection
//   priority (swisstopo on top, the fallback at the bottom), so a Leaflet map shows each country's official map side
//   by side without re-styling when the centre crosses a border (src/ui/map draws it). `mosaicAttributions` credits
//   the providers actually visible in a viewport.

import type { EpochMs, GeoBounds, GeoCoordinate, OfflinePackageLevel, OfflineRegion, ProtectedArea, TrailRoute } from '../core/models';
import { offlineCorridorHalfWidth, offlineLevelZoomRange } from '../core/domain';
import { boundsCenter, boundsExpanded, boundsIntersect, boundsOf, boundsUnion, isValidCoordinate, simplify } from '../core/geo';
import { CoverageArea, coverageMarginMeters } from '../offline/CoverageArea';
import { layerCoverageArea, type TileLayerDescriptor } from '../offline/TileLayerDescriptor';
import type { MapLayerPreferences } from './MapLayer';
import { OpenStreetMapProvider } from './OpenStreetMapProvider';
import type { RegionalTileMapProvider } from './RegionalTileMapProvider';
import { RouteRegionSplitter } from './RouteRegionSplitter';
import { SwisstopoMapProvider } from './SwisstopoMapProvider';
import type { MapAttribution, MapStyle, TrailMapProvider } from './TrailMapProvider';

/** One raster layer of the mosaic (bottom → top order in `MapMosaic.layers`). */
export interface MosaicLayer {
  layer: TileLayerDescriptor;
  providerID: string;
  /** `fallback`: the worldwide fallback base map; `base`: a provider's base map; `overlay`: a raster overlay. */
  role: 'fallback' | 'base' | 'overlay';
  /** Where tiles are requested (tiles within `coverageMarginMeters` count); `CoverageArea.worldwide` for the fallback. */
  coverage: CoverageArea;
  /** Coverages of opaque base maps drawn above this base layer: a tile entirely inside one of them is not requested. */
  occludedBy: CoverageArea[];
  /** Credits owed while this layer is visible. */
  attributions: MapAttribution[];
}

export interface MapMosaic {
  /** Bottom → top: fallback base, provider bases (lowest priority first), then overlays. */
  layers: MosaicLayer[];
}

export class CompositeMapProvider implements TrailMapProvider {
  readonly id = 'composite';
  /** The Swiss provider (checked first). */
  readonly swiss: SwisstopoMapProvider;
  /** The final fallback provider (OpenStreetMap, never cached). */
  readonly fallback: OpenStreetMapProvider;
  /** Regional official providers, in selection order. */
  readonly regional: RegionalTileMapProvider[];

  /**
   * Second-pass selection margin (m): covers the coast simplification of the bundled outlines (≤ 4.5 km for the USA,
   * ≤ 3 km for Norway, 1.5 km elsewhere) and lets a neighbouring national map serve border areas of countries without
   * their own provider.
   */
  static readonly nearCoverageMarginMeters = 3_000;
  /** Simplification tolerance of the route before the corridor test (m); added to the corridor width. */
  static readonly corridorSimplificationMeters = 50;

  constructor(swiss: SwisstopoMapProvider, fallback: OpenStreetMapProvider, regional: RegionalTileMapProvider[] = []) {
    this.swiss = swiss;
    this.fallback = fallback;
    this.regional = regional;
  }

  /** Names of all providers, in selection order ("swisstopo / … / OpenStreetMap"). */
  get displayName(): string {
    return [this.swiss.displayName, ...this.regional.map((r) => r.displayName), this.fallback.displayName].join(' / ');
  }

  /** Worldwide (the fallback covers everything). */
  get coverage(): GeoBounds {
    return boundsUnion(this.swiss.coverage, this.fallback.coverage);
  }

  /** The provider responsible for the centre of `bounds` (see the module comment). */
  providerFor(bounds: GeoBounds): TrailMapProvider {
    return this.providerAt(boundsCenter(bounds));
  }

  /** The provider responsible for `coordinate` (see the module comment). */
  providerAt(coordinate: GeoCoordinate): TrailMapProvider {
    const margin = CompositeMapProvider.nearCoverageMarginMeters;
    const primary = this.regional.filter((r) => r.tier === 'primary');
    const overview = this.regional.filter((r) => r.tier === 'overview');
    const swissArea = SwisstopoMapProvider.coverageArea;
    if (swissArea.contains(coordinate)) return this.swiss;
    const exact = primary.find((r) => r.coverageArea.contains(coordinate));
    if (exact) return exact;
    if (swissArea.containsWithin(coordinate, margin)) return this.swiss;
    const near = primary.find((r) => r.coverageArea.containsWithin(coordinate, margin));
    if (near) return near;
    const overviewExact = overview.find((r) => r.coverageArea.contains(coordinate));
    if (overviewExact) return overviewExact;
    const overviewNear = overview.find((r) => r.coverageArea.containsWithin(coordinate, margin));
    if (overviewNear) return overviewNear;
    return this.fallback;
  }

  /** The style of the provider responsible for the centre of `bounds`, plus the fallback credit when it is drawn. */
  mapStyle(bounds: GeoBounds, preferences: MapLayerPreferences): MapStyle {
    const provider = this.providerFor(bounds);
    const style = provider.mapStyle(bounds, preferences);
    if (!style.usesFallbackBaseMap || provider === this.fallback) return style;
    const attributions = [...style.attributions];
    for (const credit of this.fallback.attributionsFor([])) {
      if (!attributions.some((a) => a.text === credit.text)) attributions.push(credit);
    }
    return { ...style, attributions };
  }

  attributionsFor(layers: readonly TileLayerDescriptor[]): MapAttribution[] {
    const result: MapAttribution[] = [];
    for (const provider of this.allProviders()) {
      const own = layers.filter((l) => providerLayers(provider).some((p) => p.id === l.id));
      if (own.length === 0) continue;
      for (const credit of provider.attributionsFor(own)) {
        if (!result.some((a) => a.text.includes(credit.text))) result.push(credit);
      }
    }
    return result;
  }

  /**
   * Union of the offline layers of swisstopo and every regional provider (de-duplicated by id, in selection order).
   * Each layer carries its coverage, so planning a route with these layers only downloads tiles of the countries the
   * route crosses. Prefer `offlineTileLayersForRoute` when the route is known.
   */
  offlineTileLayers(level: OfflinePackageLevel): TileLayerDescriptor[] {
    return CompositeMapProvider.union([this.swiss.offlineTileLayers(level), ...this.regional.map((r) => r.offlineTileLayers(level))]);
  }

  /**
   * Union of the offline layers of every provider whose coverage the route corridor (± the level's half-width +
   * `coverageMarginMeters`) touches, in selection order, de-duplicated by id. Empty for `routeOnly` and for empty
   * routes; OpenStreetMap content is never included. (Swift `offlineTileLayers(for:route:)`.)
   */
  offlineTileLayersForRoute(level: OfflinePackageLevel, route: TrailRoute): TileLayerDescriptor[] {
    if (!offlineLevelZoomRange(level)) return [];
    return CompositeMapProvider.union(this.providersTouching(route, level).map((p) => p.offlineTileLayers(level)));
  }

  /**
   * Tile providers (swisstopo and regional, in selection order) whose coverage the route corridor of `level` touches.
   * The fallback is never listed.
   */
  providersTouching(route: TrailRoute, level: OfflinePackageLevel): TrailMapProvider[] {
    const boxes = CompositeMapProvider.corridorBoxes(route, offlineCorridorHalfWidth(level) + coverageMarginMeters);
    if (boxes.length === 0) return [];
    const result: TrailMapProvider[] = [];
    if (CompositeMapProvider.touches(SwisstopoMapProvider.coverageArea, boxes)) result.push(this.swiss);
    for (const provider of this.regional) {
      if (CompositeMapProvider.touches(provider.coverageArea, boxes)) result.push(provider);
    }
    return result;
  }

  /**
   * Regions of ≤ 40 km (stage boundaries when possible) whose size estimates include the official tiles of every
   * provider the route crosses (each counted only inside its coverage).
   */
  downloadableRegions(route: TrailRoute, level: OfflinePackageLevel, now: EpochMs): OfflineRegion[] {
    if (route.polyline.points.length === 0) return [];
    return RouteRegionSplitter.regions(route, level, this.offlineTileLayersForRoute(level, route), now);
  }

  /** Protected areas from the provider responsible for the centre of `bounds`. */
  protectedAreas(bounds: GeoBounds): Promise<ProtectedArea[]> {
    return this.providerFor(bounds).protectedAreas(bounds);
  }

  // MARK: - Mosaic (web)

  /**
   * Every provider's layers for `preferences`, each limited to its coverage (see `MapMosaic`). Base maps of providers
   * earlier in the selection order are drawn above later ones; a base tile entirely inside the coverage of an opaque
   * base drawn above it is never requested (the fallback is not fetched under swisstopo, IGN…).
   */
  mosaic(preferences: MapLayerPreferences): MapMosaic {
    const ordered: Array<RegionalTileMapProvider | SwisstopoMapProvider> = [
      this.swiss,
      ...this.regional.filter((r) => r.tier === 'primary'),
      ...this.regional.filter((r) => r.tier === 'overview'),
    ];
    const bases: MosaicLayer[] = []; // top → bottom while building
    const overlays: MosaicLayer[] = []; // top provider first while building
    const opaqueAbove: CoverageArea[] = [];
    for (const provider of ordered) {
      const area = provider instanceof SwisstopoMapProvider ? SwisstopoMapProvider.coverageArea : provider.coverageArea;
      const style = provider.mapStyle(provider.coverage, preferences);
      if (style.baseLayer) {
        bases.push({
          layer: style.baseLayer,
          providerID: provider.id,
          role: 'base',
          coverage: coverageOf(style.baseLayer, area),
          occludedBy: [...opaqueAbove],
          attributions: provider.attributionsFor([style.baseLayer]),
        });
        if (style.baseLayer.isOpaque) opaqueAbove.push(area);
      }
      overlays.push(
        ...style.rasterOverlays.map(
          (layer): MosaicLayer => ({
            layer,
            providerID: provider.id,
            role: 'overlay',
            coverage: coverageOf(layer, area),
            occludedBy: [],
            attributions: provider.attributionsFor([layer]),
          }),
        ),
      );
    }
    const fallback: MosaicLayer = {
      layer: OpenStreetMapProvider.standard,
      providerID: this.fallback.id,
      role: 'fallback',
      coverage: CoverageArea.worldwide,
      occludedBy: [...opaqueAbove],
      attributions: this.fallback.attributionsFor([]),
    };
    // Overlays: lowest-priority provider first so swisstopo's overlays end on top.
    const overlayGroups = new Map<string, MosaicLayer[]>();
    for (const o of overlays) overlayGroups.set(o.providerID, [...(overlayGroups.get(o.providerID) ?? []), o]);
    const overlaysBottomUp = [...overlayGroups.values()].reverse().flat();
    return { layers: [fallback, ...bases.reverse(), ...overlaysBottomUp] };
  }

  /**
   * Credits owed for the mosaic layers visible in `viewport` at `zoom`: a layer counts when its zoom range includes
   * `zoom` (the overview disappears beyond its maximum), its coverage meets the viewport and the viewport is not
   * entirely inside an opaque base drawn above it. Ordered like a provider style (highest-priority provider first, its
   * base map before its overlays, the fallback last); a credit contained in one already listed is skipped.
   */
  static mosaicAttributions(
    mosaic: MapMosaic,
    viewport: GeoBounds,
    zoom: number,
    maxZoomByLayer?: (layer: TileLayerDescriptor) => number,
  ): MapAttribution[] {
    const visible = mosaic.layers.filter((m) => {
      const maxZoom = maxZoomByLayer ? maxZoomByLayer(m.layer) : Infinity;
      if (zoom > maxZoom) return false;
      if (!m.coverage.isWorldwide && !m.coverage.intersects(boundsExpanded(viewport, coverageMarginMeters))) return false;
      return !m.occludedBy.some((area) => area.relation(viewport) === 'inside');
    });
    const rank = new Map<string, number>();
    mosaic.layers
      .filter((m) => m.role !== 'overlay')
      .reverse()
      .forEach((m, i) => {
        if (!rank.has(m.providerID)) rank.set(m.providerID, i);
      });
    const ordered = visible
      .map((m, index) => ({ m, index }))
      .sort(
        (a, b) =>
          (rank.get(a.m.providerID) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.m.providerID) ?? Number.MAX_SAFE_INTEGER) ||
          (a.m.role === 'overlay' ? 1 : 0) - (b.m.role === 'overlay' ? 1 : 0) ||
          a.index - b.index,
      );
    const result: MapAttribution[] = [];
    for (const { m } of ordered) {
      for (const credit of m.attributions) {
        if (!result.some((a) => a.text.includes(credit.text))) result.push(credit);
      }
    }
    return result;
  }

  // MARK: - Internals

  private allProviders(): TrailMapProvider[] {
    return [this.swiss, ...this.regional, this.fallback];
  }

  /** One box per (simplified) route segment, expanded by the corridor half-width: a superset of the corridor. */
  static corridorBoxes(route: TrailRoute, halfWidthMeters: number): GeoBounds[] {
    const points = route.polyline.points.filter((p) => isValidCoordinate(p.coordinate));
    const first = points[0];
    if (!first) return [];
    const reach = Math.max(0, halfWidthMeters) + CompositeMapProvider.corridorSimplificationMeters;
    const simplified = simplify(points, CompositeMapProvider.corridorSimplificationMeters).map((p) => p.coordinate);
    if (simplified.length <= 1) {
      const box = boundsOf([first.coordinate]);
      return box ? [boundsExpanded(box, reach)] : [];
    }
    const boxes: GeoBounds[] = [];
    for (let i = 1; i < simplified.length; i++) {
      const box = boundsOf([simplified[i - 1], simplified[i]]);
      if (box) boxes.push(boundsExpanded(box, reach));
    }
    return boxes;
  }

  static touches(area: CoverageArea, boxes: readonly GeoBounds[]): boolean {
    if (area.isWorldwide) return true;
    const box = area.boundingBox;
    if (!box) return false;
    return boxes.some((b) => boundsIntersect(b, box) && area.intersects(b));
  }

  static union(groups: readonly (readonly TileLayerDescriptor[])[]): TileLayerDescriptor[] {
    const seen = new Set<string>();
    const result: TileLayerDescriptor[] = [];
    for (const group of groups) {
      for (const layer of group) {
        if (seen.has(layer.id)) continue;
        seen.add(layer.id);
        result.push(layer);
      }
    }
    return result;
  }
}

function providerLayers(provider: TrailMapProvider): readonly TileLayerDescriptor[] {
  if (provider instanceof SwisstopoMapProvider) return SwisstopoMapProvider.allLayers;
  if (provider instanceof OpenStreetMapProvider) return [OpenStreetMapProvider.standard];
  return (provider as RegionalTileMapProvider).allLayers;
}

/** A layer's own coverage when it declares one (shared instance for the provider's own area). */
function coverageOf(layer: TileLayerDescriptor, providerArea: CoverageArea): CoverageArea {
  if (layer.coverage === undefined && layer.coveragePolygons === undefined) return CoverageArea.worldwide;
  const own = layerCoverageArea(layer);
  return own.equals(providerArea) ? providerArea : own;
}
